import type { TAssignableRole } from '@conversed/shared/src/central/types/role.types';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  buildRolesForChange,
  getSelectableMemberRoles,
  hasSameRoleIds,
} from '../users.helpers';

const ROLE_ID_KEY_SEPARATOR = '|';

type TUseRoleDraftParams = {
  roles: TAssignableRole[];
  selectedRoleIds: string[];
  optionRoleIds: string[];
  isDisabled: boolean;
  onCommit: (roleIds: string[]) => void;
};

/**
 * Holds the roles that the user ticks while the dropdown is open. The draft
 * goes to `onCommit` when the dropdown closes, only when it changed and is not
 * empty.
 */
export const useRoleDraft = ({
  roles,
  selectedRoleIds,
  optionRoleIds,
  isDisabled,
  onCommit,
}: TUseRoleDraftParams) => {
  // * The draft depends on a key of the role ids, not on the array. A new
  // * store array with the same roles then keeps an open draft.
  const savedRoleIdsKey = getSelectableMemberRoles({
    memberRoles: selectedRoleIds,
    selectableRoleIds: optionRoleIds,
  }).join(ROLE_ID_KEY_SEPARATOR);

  const savedRoleIds = useMemo(() => {
    if (!savedRoleIdsKey) {
      return [];
    }

    return savedRoleIdsKey.split(ROLE_ID_KEY_SEPARATOR);
  }, [savedRoleIdsKey]);

  const [draftRoleIds, setDraftRoleIds] = useState<string[]>(savedRoleIds);

  // * While the save runs, the draft keeps the sent roles. After the save, the
  // * draft takes the saved roles again.
  useEffect(() => {
    if (isDisabled) {
      return;
    }

    setDraftRoleIds(savedRoleIds);
  }, [savedRoleIds, isDisabled]);

  // * The member keeps every role that the viewer cannot select. So the draft
  // * can be empty while the member still has a role.
  const isDraftWithoutRoles = useMemo(() => {
    return (
      buildRolesForChange({
        memberRoles: selectedRoleIds,
        selectableRoleIds: optionRoleIds,
        newRoleIds: draftRoleIds,
      }).length === 0
    );
  }, [draftRoleIds, optionRoleIds, selectedRoleIds]);

  // * The label shows the draft and the roles that the viewer cannot select.
  // * So the label matches the ticks in the open list.
  const draftLabelRoles = useMemo(() => {
    return roles.filter(
      (role) =>
        draftRoleIds.includes(role.id) ||
        (selectedRoleIds.includes(role.id) && !optionRoleIds.includes(role.id)),
    );
  }, [draftRoleIds, optionRoleIds, roles, selectedRoleIds]);

  const commitDraft = () => {
    if (isDraftWithoutRoles || hasSameRoleIds(draftRoleIds, savedRoleIds)) {
      setDraftRoleIds(savedRoleIds);

      return;
    }

    onCommit(draftRoleIds);
  };

  // * The listbox stops the Escape event before an `onKeyDown` handler gets
  // * it. So this handler runs in the capture phase.
  const discardDraftOnEscape = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape') {
      return;
    }

    setDraftRoleIds(savedRoleIds);
  };

  return {
    savedRoleIds,
    draftRoleIds,
    setDraftRoleIds,
    isDraftWithoutRoles,
    draftLabelRoles,
    commitDraft,
    discardDraftOnEscape,
  };
};
